import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  async function signup(email, password, role = 'user', username = '') {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Store user data in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        email: user.email,
        username: username,
        role: role,
        createdAt: new Date()
      });
      
      // Send welcome email
      await sendWelcomeEmail(user.email, username, role);
      
      return userCredential;
    } catch (error) {
      throw error;
    }
  }

  async function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  async function logout() {
    return signOut(auth);
  }

  async function sendWelcomeEmail(email, username, role) {
    try {
      console.log(`🚀 === WELCOME EMAIL DEBUG START ===`);
      console.log(`📧 Attempting to send welcome email to ${email} for user ${username} with role ${role}`);
      
      // Import Firebase Functions
      console.log('📦 Importing Firebase Functions...');
      const { getFunctions, httpsCallable } = await import('firebase/functions');
      console.log('✅ Firebase Functions imported successfully');
      
      const functions = getFunctions();
      console.log('🔧 Firebase Functions initialized:', functions);
      
      // Log function configuration
      console.log('⚙️ Functions config:', {
        region: functions.region,
        app: functions.app?.name,
        projectId: functions.app?.options?.projectId
      });
      
      // First test the email configuration
      console.log('🧪 Testing email configuration...');
      const testConfig = httpsCallable(functions, 'testEmailConfig');
      console.log('✅ testEmailConfig callable created');
      
      const testResult = await testConfig();
      console.log('📊 Email config test result:', testResult.data);
      
      if (!testResult.data.success) {
        console.error('❌ Email configuration test failed:', testResult.data.error);
        return;
      }
      
      console.log('✅ Email configuration test passed');
      
      // Call the sendWelcomeEmail function
      console.log('📞 Creating sendWelcomeEmail callable...');
      const sendEmail = httpsCallable(functions, 'sendWelcomeEmail');
      console.log('✅ sendWelcomeEmail callable created');
      
      console.log('📤 Calling sendWelcomeEmail function...');
      console.log('📋 Function parameters:', { email, username, role });
      
      // Add timeout and detailed error handling
      const result = await Promise.race([
        sendEmail({
          email: email,
          username: username,
          role: role
        }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Function call timeout after 30 seconds')), 30000)
        )
      ]);
      
      console.log('📥 Function call result:', result);
      
      if (result.data.success) {
        console.log(`✅ Welcome email sent successfully to ${email} for user ${username}`);
      } else {
        console.error('❌ Function returned error:', result.data.error);
        console.error('Error details:', result.data.details);
      }
      
      console.log('🏁 === WELCOME EMAIL DEBUG END ===');
      
    } catch (error) {
      console.error('💥 === ERROR IN WELCOME EMAIL ===');
      console.error('❌ Error sending welcome email:', error);
      console.error('🚨 Error type:', error.constructor.name);
      console.error('📋 Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        stack: error.stack,
        name: error.name
      });
      
      // Check if it's a CORS error
      if (error.message && error.message.includes('CORS')) {
        console.error('🌐 CORS ERROR DETECTED - This is a server configuration issue');
      }
      
      // Check if it's a network error
      if (error.code === 'functions/internal') {
        console.error('🔌 INTERNAL FUNCTION ERROR - Check Firebase Function logs');
      }
      
      console.error('🏁 === ERROR DEBUG END ===');
      // Don't throw error to prevent registration failure
    }
  }

  async function fetchUserData(uid) {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        return userDoc.data();
      }
      return { role: 'user', username: 'User' }; // default data
    } catch (error) {
      console.error('Error fetching user data:', error);
      return { role: 'user', username: 'User' };
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        const userData = await fetchUserData(user.uid);
        setUserRole(userData.role);
        setUserData(userData);
      } else {
        setCurrentUser(null);
        setUserRole(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userData,
    signup,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
